/**
 * Display-only preview of a client's PDF filename template. The backend
 * (invoice-attachment-filename.ts) is the source of truth for the real
 * download name; this just mirrors the token substitution + sanitization so
 * the client form and invoice-detail affordance can show what the result
 * looks like. Keep the token set, month names, and sanitizing in sync with the
 * backend.
 */

export const PDF_FILENAME_TOKENS = [
  "tipo",
  "serie",
  "numero",
  "fecha",
  "mes",
  "cliente",
  "rut",
] as const;

export type PdfFilenameTokenValues = Record<
  (typeof PDF_FILENAME_TOKENS)[number],
  string
>;

export type PdfFilenameLanguage = "es" | "en";

const MONTH_NAMES: Record<PdfFilenameLanguage, readonly string[]> = {
  es: [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Setiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

// A fixed, clearly-illustrative sample date (NOT today). The real filename
// always uses each invoice's own issue date — this is preview-only.
const SAMPLE_DATE = "2026-03-15";

function monthName(isoDate: string, lang: PdfFilenameLanguage): string {
  const m = /^\d{4}-(\d{2})/.exec(isoDate);
  if (!m) return "";
  return MONTH_NAMES[lang][Number(m[1]) - 1] ?? "";
}

function sanitizeSegment(s: string): string {
  return s.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 32);
}

function sanitizeWhole(s: string): string {
  return s
    .replace(/\.pdf$/i, "")
    .replace(/[^a-zA-Z0-9._\- ]+/g, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s_]+|[.\s_]+$/g, "")
    .slice(0, 120);
}

export interface PreviewPdfFilenameOptions {
  language?: PdfFilenameLanguage;
  cliente?: string;
  rut?: string;
}

/**
 * Resolve `template` to a filename for preview. Pass real token values (the
 * client's name/rut from the form) and language to override the samples.
 * Returns null when the template is empty (caller shows the default name).
 */
export function previewPdfFilename(
  template: string | null | undefined,
  opts?: PreviewPdfFilenameOptions,
): string | null {
  const tpl = template?.trim();
  if (!tpl) return null;
  const language: PdfFilenameLanguage = opts?.language === "en" ? "en" : "es";
  const tokens: PdfFilenameTokenValues = {
    tipo: "101",
    serie: "A",
    numero: "1234",
    fecha: SAMPLE_DATE,
    mes: monthName(SAMPLE_DATE, language),
    cliente: opts?.cliente?.trim() || "Cliente",
    rut: opts?.rut?.trim() || "212345670019",
  };
  // {{token}} (matching the email convention) plus legacy {token}.
  const replaceToken = (
    _m: string,
    key: (typeof PDF_FILENAME_TOKENS)[number],
  ): string => sanitizeSegment(tokens[key] ?? "");
  const resolved = tpl
    .replace(/\{\{(tipo|serie|numero|fecha|mes|cliente|rut)\}\}/g, replaceToken)
    .replace(/\{(tipo|serie|numero|fecha|mes|cliente|rut)\}/g, replaceToken);
  const cleaned = sanitizeWhole(resolved);
  if (!cleaned) return null;
  return `${cleaned}.pdf`;
}
